import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { FeaturedCampaigns } from "../components/FeaturedCampaigns";
import { EndingSoon } from "../components/EndingSoon";
import { GeneralFundBand } from "../components/GeneralFundBand";
import { CreateCampaign } from "../components/CreateCampaign";
import { DonateDialog } from "../components/DonateDialog";
import { FadeUp } from "../components/Reveal";
import { PageAccent } from "../components/PageAccent";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * Demo — the prototype-campaigns landing for donors who chose
 * "Preview the donor experience" on `/preview`.
 *
 * Bugfix (2026-07-19c): the `/demo` route used to render `<Landing />`,
 * which meant a donor clicking through Preview → Donor experience landed
 * on the marketing homepage all over again. This page is a lean,
 * campaigns-first landing — no re-hero, no manifesto pitch — so the
 * donor arrives directly at the sample campaigns.
 *
 * `?flow=student` still auto-opens the Create Campaign wizard so the
 * student-preview entry from `/preview` continues to work.
 */
export default function Demo() {
  useNoIndex();
  const [createOpen, setCreateOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const openDonate = () => setDonateOpen(true);

  // Auto-open the Create Campaign wizard when arriving via
  // /demo?flow=student (from the Preview page's "student experience" CTA).
  useEffect(() => {
    if (searchParams.get("flow") === "student") {
      setCreateOpen(true);
    }
  }, [searchParams]);

  return (
    <main data-testid="demo-page" className="bg-sand">
      <Navbar
        onStart={openDonate}
        onDonate={openDonate}
        primaryLabel="Donate"
        secondaryLabel={null}
      />

      {/* Slim editorial intro — sets the frame in ~10 words, no re-marketing */}
      <section className="mx-auto max-w-7xl px-6 pb-6 pt-32 lg:px-8 lg:pt-44">
        <FadeUp tier="sm">
          <PageAccent className="mb-5" />
        </FadeUp>
        <FadeUp>
          <span
            data-testid="demo-eyebrow"
            className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine"
          >
            Prototype · Donor experience
          </span>
          <h1
            data-testid="demo-heading"
            className="mt-4 max-w-3xl font-serif text-3xl lg:text-[3.5rem] leading-[0.95] tracking-tight text-ink"
          >
            Meet the students.
          </h1>
          <p
            data-testid="demo-sub"
            className="mt-6 max-w-xl text-base leading-relaxed text-olive"
          >
            Every profile below is a demo so you can walk the flow. Browse a story, follow a goal, tap <em className="italic">Donate</em> — the whole path works, minus the real payment step.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to="/demo/story"
              data-testid="demo-story-cta"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-base font-semibold text-sand transition hover:bg-forest"
            >
              <PlayCircle className="h-4 w-4" strokeWidth={2} />
              Watch the student story (60s)
            </Link>
            <Link
              to="/demo/donor-story"
              data-testid="demo-donor-story-cta"
              className="inline-flex items-center gap-2 rounded-full border border-ink px-5 py-2.5 text-base font-semibold text-ink transition hover:bg-ink hover:text-sand"
            >
              <PlayCircle className="h-4 w-4" strokeWidth={2} />
              Watch the donor story (60s)
            </Link>
            <Link
              to="/demo/admin-story"
              data-testid="demo-admin-story-cta"
              className="inline-flex items-center gap-2 rounded-full border border-ink px-5 py-2.5 text-base font-semibold text-ink transition hover:bg-ink hover:text-sand"
            >
              <PlayCircle className="h-4 w-4" strokeWidth={2} />
              Watch the admin walkthrough (60s)
            </Link>
          </div>
        </FadeUp>
      </section>

      {/* Sample campaigns — the real substance of the donor demo */}
      <FeaturedCampaigns onDonate={openDonate} />
      <EndingSoon />
      <GeneralFundBand onDonate={openDonate} />

      {/* No FAQ band here: the nav and footer both link to /faq. */}
      <Footer variant="minimal" />

      <CreateCampaign open={createOpen} onOpenChange={setCreateOpen} />
      <DonateDialog open={donateOpen} onOpenChange={setDonateOpen} />
    </main>
  );
}
